import { useState } from 'react';
import { OperationVariables, useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { ChecklistObservation, Student, StudentChecklistOutcome } from 'types';
import AssessmentScore from 'components/common/AssessmentScore';
import CREATE_STUDENT_CHECKLIST_OUTCOME from 'graph/mutations/studentChecklistOutcome/create-student-checklist-outcome';
import UPDATE_STUDENT_CHECKLIST_OUTCOME from 'graph/mutations/studentChecklistOutcome/update-student-checklist-outcome';

const useStyles = makeStyles((theme: Theme) => {
  return {
    gradeTooltip: {
      position: 'relative',
      backgroundColor: theme.palette.primary.main,
      '& .MuiButton-root': {
        color: 'white',
      },
    },
    tooltipPointer: {
      content: '',
      position: 'absolute',
      bottom: '-0.5em',
      left: '45%',
      backgroundColor: theme.palette.primary.main,
      width: '1em',
      height: '1em',
      transform: 'rotate(45deg)',
    },
    pointer: {
      cursor: 'pointer',
    },
  };
});

const StudentObservationRow = (props: {
  students: Student[];
  checklistObservation: ChecklistObservation;
  outcomes: StudentChecklistOutcome[];
  checklistId: number;
}) => {
  const [outcomes, setOutcomes] = useState<StudentChecklistOutcome[]>(props.outcomes ?? []);
  const [anchorEl, setAnchorEl] = useState<HTMLTableCellElement | null>(null);
  const open = Boolean(anchorEl);
  const [anchorType, setAnchorType] = useState<string | null>(null);
  const [anchorId, setAnchorId] = useState<number | null>(null);

  const [updateOutcome] = useMutation(UPDATE_STUDENT_CHECKLIST_OUTCOME);
  const [createOutcome] = useMutation(CREATE_STUDENT_CHECKLIST_OUTCOME);

  const classes = useStyles();

  const createOrUpdateOutcomeHelper = (
    score: number | null,
    outcomes: StudentChecklistOutcome[],
    studentId: number
  ) => {
    const tempOutcomes = outcomes;
    const outcomeIndex = tempOutcomes.findIndex((v) => v.studentId === studentId);
    if (outcomeIndex === -1) {
      const outcomeToCreate = {
        studentId: studentId,
        checklistId: props.checklistId,
        observationId: props.checklistObservation.observation.id,
        score: score,
      } as StudentChecklistOutcome;
      tempOutcomes.push(outcomeToCreate);
      const variables: OperationVariables = {
        data: outcomeToCreate,
      };
      createOutcome({ variables: variables });
    } else if (tempOutcomes[outcomeIndex].score !== score) {
      tempOutcomes[outcomeIndex] = { ...tempOutcomes[outcomeIndex], score: score };
      const variables: OperationVariables = {
        where: {
          studentChecklistOutcomeId: {
            studentId: studentId,
            checklistId: props.checklistId,
            observationId: props.checklistObservation.observation.id,
          },
        },
        data: {
          score: score,
        },
      };
      updateOutcome({ variables: variables });
    }
    return tempOutcomes;
  };

  const createOrUpdateOutcome = (score: number | null) => {
    if (anchorType === 'observation') {
      let tempOutcomes = [...outcomes];
      props.students.forEach((student) => {
        tempOutcomes = [...createOrUpdateOutcomeHelper(score, [...tempOutcomes], student.id)];
      });
      setOutcomes(tempOutcomes);
    } else if (anchorType === 'student') {
      setOutcomes(createOrUpdateOutcomeHelper(score, [...outcomes], anchorId!));
    }
  };

  const renderScore = (score: number | undefined | null) => {
    switch (score) {
      case null:
        return '/';
      case 1:
        return <RemoveIcon />;
      case 2:
        return <CheckIcon />;
      case 3:
        return (
          <>
            <CheckIcon />
            <AddIcon />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <TableRow key={'observation-row-' + props.checklistObservation.observation.id}>
      <Popper open={open && anchorType === 'observation'} anchorEl={anchorEl} placement="top">
        <ClickAwayListener
          onClickAway={() => {
            setAnchorEl(null);
            setAnchorType(null);
          }}
        >
          <Box className={classes.gradeTooltip}>
            <Button onClick={() => createOrUpdateOutcome(3)}>{renderScore(3)}</Button>
            <Button onClick={() => createOrUpdateOutcome(2)}>{renderScore(2)}</Button>
            <Button onClick={() => createOrUpdateOutcome(1)}>{renderScore(1)}</Button>
            <Button onClick={() => createOrUpdateOutcome(null)}>{renderScore(null)}</Button>
            <span className={classes.tooltipPointer}></span>
          </Box>
        </ClickAwayListener>
      </Popper>
      <TableCell
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          setAnchorType('observation');
        }}
        className={classes.pointer}
      >
        <Typography variant="body1">{props.checklistObservation.observation.title}</Typography>
      </TableCell>
      {props.students?.map((student) => {
        return (
          <TableCell
            key={'student-observation-cell-' + student.id}
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
              setAnchorType('student');
              setAnchorId(student.id);
            }}
            className={classes.pointer}
          >
            <Popper
              open={open && anchorType === 'student' && anchorId === student.id}
              anchorEl={anchorEl}
              placement="top"
            >
              <ClickAwayListener
                onClickAway={() => {
                  setAnchorEl(null);
                  setAnchorType(null);
                  setAnchorId(null);
                }}
              >
                <Box className={classes.gradeTooltip}>
                  <Button onClick={() => createOrUpdateOutcome(3)}>{renderScore(3)}</Button>
                  <Button onClick={() => createOrUpdateOutcome(2)}>{renderScore(2)}</Button>
                  <Button onClick={() => createOrUpdateOutcome(1)}>{renderScore(1)}</Button>
                  <Button onClick={() => createOrUpdateOutcome(null)}>{renderScore(null)}</Button>
                  <span className={classes.tooltipPointer}></span>
                </Box>
              </ClickAwayListener>
            </Popper>
            <AssessmentScore score={outcomes.find((v) => v.studentId === student.id)?.score} />
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default StudentObservationRow;
